'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

// Operator-facing player for a single rrweb recording. Loaded by Channels
// Dashboard when a lead row's recording_id is non-null. Spec:
// docs/superpowers/specs/2026-05-03-session-replay-research.md
//
// Mirrors FoundLocal's apps/dashboard/app/(dashboard)/sitetrack/sessions/[id]/page.tsx.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '@/lib/authFetch';
import { Copy, Trash2, X } from 'lucide-react';
import RecordingNotes from './RecordingNotes';

const API = process.env.NEXT_PUBLIC_API_URL || 'https://api.crewsyncautomation.io';

type Recording = {
  id: string; lead_id: string | null; session_id: string;
  started_at: string; ended_at: string | null; duration_sec: number | null;
  viewport_w: number | null; viewport_h: number | null;
  device_type: string | null; browser: string | null; os: string | null;
  storage_path: string | null; size_bytes: number; truncated: boolean;
  event_summary: any; expires_at: string;
};

function getViewport(events: any[]): { w: number; h: number } {
  const meta = events.find(e => e?.type === 4);
  if (meta?.data?.width > 0 && meta?.data?.height > 0) {
    const w = meta.data.width;
    const maxH = w <= 768 ? 812 : 800;
    return { w, h: Math.min(meta.data.height, maxH) };
  }
  return { w: 1280, h: 800 };
}

export default function RecordingPlayer({ recordingId, onClose }: { recordingId: string; onClose: () => void }) {
  const [data, setData] = useState<{ recording: Recording; events: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(`${API}/api/widget-recordings/${recordingId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recordingId]);

  const viewport = useMemo(() => data?.events ? getViewport(data.events) : { w: 1280, h: 800 }, [data?.events]);
  const expired = data?.recording?.storage_path === null && (data?.events?.length ?? 0) === 0;

  const initPlayer = useCallback(() => {
    if (!data?.events?.length || !playerContainerRef.current) return;
    if (playerRef.current) { try { playerRef.current.$destroy?.(); } catch {} playerRef.current = null; }
    playerContainerRef.current.innerHTML = '';

    import('rrweb-player').then(({ default: Replayer }) => {
      if (!document.querySelector('link[href*="rrweb-player"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/rrweb-player@1.0.0-alpha.4/dist/style.css';
        document.head.appendChild(link);
      }
      if (!playerContainerRef.current) return;
      const containerW = Math.max(playerContainerRef.current.offsetWidth - 8, 300);
      const scale = Math.min(containerW / viewport.w, 1.0);
      try {
        playerRef.current = new Replayer({
          target: playerContainerRef.current,
          props: {
            events: data.events,
            width:  Math.round(viewport.w * scale),
            height: Math.round(viewport.h * scale),
            autoPlay: false,
            showController: true,
            speed: 1,
            speedOption: [1, 2, 4, 8],
            showWarning: false,
            showDebug: false,
          },
        });
      } catch (e) { console.error('[player] init failed', e); }
    });
  }, [data?.events, viewport]);

  useEffect(() => {
    const t = setTimeout(initPlayer, 150);
    return () => {
      clearTimeout(t);
      if (playerRef.current) { try { playerRef.current.$destroy?.(); } catch {} playerRef.current = null; }
    };
  }, [initPlayer]);

  async function createShareLink() {
    const r = await authFetch(`${API}/api/widget-recordings/${recordingId}/share`, { method: 'POST' });
    if (!r.ok) return;
    const d = await r.json();
    if (d?.data?.url) {
      setShareUrl(d.data.url);
      try { await navigator.clipboard.writeText(d.data.url); } catch {}
    }
  }

  async function deleteRecording() {
    if (!confirm('Delete this recording? This cannot be undone.')) return;
    const r = await authFetch(`${API}/api/widget-recordings/${recordingId}`, { method: 'DELETE' });
    if (r.ok) onClose();
  }

  return (
    <div className="rounded-xl border border-[--border-primary] bg-[--bg-card] overflow-hidden flex flex-col" style={{ minHeight: 460 }}>
      <div className="px-4 py-3 border-b border-[--border-primary] flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[--text-primary]">Session replay</h3>
        <div className="flex items-center gap-1.5">
          <button
            onClick={createShareLink}
            disabled={loading || expired}
            title={expired ? 'Recording expired' : 'Copy 24-hour share link'}
            className="px-2 py-1 text-[11px] rounded border border-[--border-subtle] text-[--text-secondary] hover:bg-[--bg-tint] disabled:opacity-50 inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> {shareUrl ? 'Copied' : 'Share'}
          </button>
          <button
            onClick={deleteRecording}
            disabled={loading}
            title="Delete recording"
            className="px-2 py-1 text-[11px] rounded border border-[--border-subtle] text-[--text-secondary] hover:bg-red-50 hover:text-red-600 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <button onClick={onClose} className="p-1 rounded hover:bg-[--bg-tint] text-[--text-tertiary]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 p-4">
        {loading && <div className="text-sm text-[--text-tertiary]">Loading recording…</div>}
        {!loading && expired && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center">
            <button disabled className="px-3 py-1.5 text-xs rounded bg-gray-200 text-gray-500 cursor-not-allowed mb-2">
              ▶ Recording expired
            </button>
            <p className="text-xs text-amber-900">The video has been deleted per your retention policy. Notes + metadata below are kept.</p>
          </div>
        )}
        {!loading && !expired && data?.events?.length === 0 && (
          <div className="text-sm text-[--text-tertiary] text-center py-8">No events yet for this session.</div>
        )}
        {!loading && data?.events && data.events.length > 0 && (
          <div ref={playerContainerRef} className="w-full" />
        )}
      </div>

      {data?.recording && (
        <RecordingMeta recording={data.recording} leadId={data.recording.lead_id} />
      )}
    </div>
  );
}

function RecordingMeta({ recording, leadId }: { recording: Recording; leadId: string | null }) {
  const dur = recording.duration_sec
    ? `${Math.floor(recording.duration_sec / 60)}m ${recording.duration_sec % 60}s`
    : recording.ended_at
      ? `${Math.max(1, Math.floor((new Date(recording.ended_at).getTime() - new Date(recording.started_at).getTime()) / 1000))}s`
      : '—';
  return (
    <div className="border-t border-[--border-subtle] px-4 py-3 grid grid-cols-2 gap-3 text-[11px] text-[--text-secondary]">
      <div><span className="text-[--text-tertiary]">When </span>{new Date(recording.started_at).toLocaleString()}</div>
      <div><span className="text-[--text-tertiary]">Duration </span>{dur}</div>
      <div><span className="text-[--text-tertiary]">Device </span>{recording.device_type || '—'} {recording.browser || ''}</div>
      <div><span className="text-[--text-tertiary]">Viewport </span>{recording.viewport_w}×{recording.viewport_h}</div>
      {recording.truncated && (
        <div className="col-span-2 text-amber-700">⚠ Recording was truncated at the 2 MB cap.</div>
      )}
      {leadId && (
        <RecordingNotes leadId={leadId} recordingId={recording.id} />
      )}
    </div>
  );
}

